import { useState, type FormEvent } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { ApiError } from '../api/errors'
import { loginSecondFactor, normalizeCode, type LoginTicket, type SessionUser } from '../api/auth'
import {
  changePasswordFirstAccess,
  confirmEmailVerification,
  confirmMfaEnrollment,
  groupSecret,
  passwordChangeErrorCode,
  secretFromOtpauth,
  startEmailVerification,
  startMfaEnrollment,
} from '../api/firstAccess'
import { TotpQr } from '../components/TotpQr'
import { t } from '../i18n'

/**
 * TELA 2 - PRIMEIRO ACESSO (S32; recorte S27-6, tela 2 de 6).
 *
 * Aberta pela tela 1 conforme o `next_step` do `/login` -- no MESMO componente,
 * sem rota nova: o ticket viaja por propriedade, nunca por URL nem por
 * `history.state` (que o navegador persiste no historico da sessao).
 *
 *   password_change  -> A. senha nova  -> /password/change(T1) -> ticket T2 (F-8)
 *   mfa_enroll       -> B. QR          -> /mfa/enroll(T)
 *                       C. 1o codigo   -> /mfa/confirm(T)      -> 10 codigos, UMA vez
 *                       D. codigo NOVO -> /login/mfa(T)        -> sessao (cookie HttpOnly)
 *   mfa_email_verify -> E. [Enviar codigo] -> /mfa/email/start(T)   (s33, ADR-0031 M-5 passo 3)
 *                       F. 8 digitos      -> /mfa/email/confirm(T) -> 10 codigos UMA vez
 *                                            + SESSAO ja aberta (S33-5) -> "guardei" -> entra.
 *                       E o caminho do FUNCIONARIO DO CLIENTE (S16-1). NUNCA o /mfa/enroll,
 *                       que converteria a pessoa em TOTP (A129).
 *
 * SEGREDOS (S32-3, S32-4; ADR-0030 D13): a senha provisoria vem da tela 1 so em
 * memoria e e esquecida apos a troca; a URI (segredo TOTP) e os codigos vivem em
 * memoria, com `gcTime: 0` e `reset()` para nao ficarem no MutationCache. Nada
 * vai a storage, URL, console ou telemetria. O mesmo vale (s33) para o codigo do
 * e-mail e para os tokens da sessao aberta pelo confirm: a SPA so guarda o usuario.
 */

type Stage =
  | { kind: 'password'; ticket: LoginTicket }
  | { kind: 'enroll'; ticket: LoginTicket }
  | { kind: 'codes'; ticket: LoginTicket; codes: string[]; user?: SessionUser }
  | { kind: 'signin'; ticket: LoginTicket; email?: boolean }
  | { kind: 'emailVerify'; ticket: LoginTicket }
  | { kind: 'unsupported' }

/**
 * Para onde o `next_step` leva. `mfa_email` depois da troca e o caso do usuario
 * JA inscrito por e-mail cuja senha foi resetada: o codigo de 8 digitos foi
 * emitido sob o ticket ORIGINAL (T1, M-7: um envio por ticket) e o ticket
 * rotacionado nao o destranca -- por isso o passo final usa o T1 (que continua
 * valido: rotacionar nao revoga, F-8). Declarado no bloco da s32.
 */
function stageFor(next: string, rotated: LoginTicket, original: LoginTicket): Stage {
  if (next === 'mfa_enroll') return { kind: 'enroll', ticket: rotated }
  if (next === 'mfa_totp') return { kind: 'signin', ticket: rotated }
  if (next === 'mfa_email') return { kind: 'signin', ticket: original, email: true }
  if (next === 'mfa_email_verify') return { kind: 'emailVerify', ticket: rotated }
  return { kind: 'unsupported' }
}

function initialStage(ticket: LoginTicket): Stage {
  if (ticket.nextStep === 'password_change') return { kind: 'password', ticket }
  if (ticket.nextStep === 'mfa_enroll') return { kind: 'enroll', ticket }
  if (ticket.nextStep === 'mfa_email_verify') return { kind: 'emailVerify', ticket }
  return { kind: 'unsupported' }
}

function stepErrorMessage(error: unknown, codeStep: boolean, emailCode = false): string {
  if (!(error instanceof ApiError)) return t.error.genericMessage
  if (error.isNetworkError) return t.error.networkMessage
  if (error.status === 503) return t.login.errors.mfaUnavailable
  if (error.isUnauthorized && emailCode) return t.firstAccess.emailInvalidCode
  if (error.isUnauthorized) return codeStep ? t.firstAccess.invalidCode : t.firstAccess.expired
  const code = passwordChangeErrorCode(error)
  if (code) return t.firstAccess.errors[code]
  return t.error.genericMessage
}

export function FirstAccessFlow({
  ticket,
  currentPassword,
  onPasswordConsumed,
  onRestart,
  onSignedIn,
}: {
  ticket: LoginTicket
  currentPassword: string
  onPasswordConsumed: () => void
  onRestart: () => void
  onSignedIn: (user: SessionUser) => void
}) {
  const [stage, setStage] = useState<Stage>(() => initialStage(ticket))
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [mismatch, setMismatch] = useState(false)
  const [code, setCode] = useState('')
  const [saved, setSaved] = useState(false)
  const [copied, setCopied] = useState(false)

  const change = useMutation({
    mutationFn: changePasswordFirstAccess,
    gcTime: 0,
    onSuccess: (rotated) => {
      setNewPassword('')
      setConfirmPassword('')
      onPasswordConsumed()
      change.reset()
      setStage(stageFor(String(rotated.nextStep), rotated, ticket))
    },
  })

  const enroll = useMutation({ mutationFn: startMfaEnrollment, gcTime: 0 })

  const confirm = useMutation({
    mutationFn: confirmMfaEnrollment,
    gcTime: 0,
    onSuccess: (r) => {
      if (stage.kind !== 'enroll') return
      setCode('')
      enroll.reset()
      confirm.reset()
      setStage({ kind: 'codes', ticket: stage.ticket, codes: r.recoveryCodes })
    },
  })

  // s33: o codigo do e-mail e os 10 codigos vivem so em memoria (gcTime 0 + reset).
  const emailStart = useMutation({ mutationFn: startEmailVerification, gcTime: 0 })

  const emailConfirm = useMutation({
    mutationFn: confirmEmailVerification,
    gcTime: 0,
    onSuccess: (r) => {
      if (stage.kind !== 'emailVerify') return
      setCode('')
      emailStart.reset()
      emailConfirm.reset()
      setStage({ kind: 'codes', ticket: stage.ticket, codes: r.recoveryCodes, user: r.user })
    },
  })

  const signin = useMutation({
    mutationFn: loginSecondFactor,
    gcTime: 0,
    onSuccess: (user) => {
      setCode('')
      onSignedIn(user)
    },
  })

  function submitPassword(e: FormEvent) {
    e.preventDefault()
    if (stage.kind !== 'password') return
    if (newPassword !== confirmPassword) {
      setMismatch(true)
      return
    }
    setMismatch(false)
    change.mutate({ ticket: stage.ticket.ticket, currentPassword, newPassword })
  }

  function submitConfirm(e: FormEvent) {
    e.preventDefault()
    if (stage.kind !== 'enroll') return
    confirm.mutate({ ticket: stage.ticket.ticket, code: normalizeCode(code) })
  }

  function submitSignin(e: FormEvent) {
    e.preventDefault()
    if (stage.kind !== 'signin') return
    signin.mutate({ ticket: stage.ticket.ticket, code: normalizeCode(code) })
  }

  function submitEmailCode(e: FormEvent) {
    e.preventDefault()
    if (stage.kind !== 'emailVerify') return
    emailConfirm.mutate({ ticket: stage.ticket.ticket, code: normalizeCode(code) })
  }

  function leaveCodes() {
    if (stage.kind !== 'codes') return
    setSaved(false)
    setCopied(false)
    // s33 (S33-5): no caminho do e-mail a sessao JA foi aberta pelo confirm -- entra
    // direto, sem pedir outro codigo (que feriria a M-7).
    if (stage.user) {
      onSignedIn(stage.user)
      return
    }
    setStage({ kind: 'signin', ticket: stage.ticket })
  }

  function copyCodes(codes: string[]) {
    void navigator.clipboard?.writeText(codes.join('\n')).then(() => setCopied(true))
  }

  function downloadCodes(codes: string[]) {
    if (typeof URL.createObjectURL !== 'function') return
    const blob = new Blob([`STRONGERH ONE - codigos de recuperacao\n\n${codes.join('\n')}\n`], {
      type: 'text/plain',
    })
    const href = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = href
    a.download = 'strongerh-codigos-de-recuperacao.txt'
    a.click()
    URL.revokeObjectURL(href)
  }

  const backButton = (
    <Button variant="text" onClick={onRestart}>
      {t.login.back}
    </Button>
  )

  if (stage.kind === 'unsupported') {
    return (
      <Stack spacing={2}>
        <Alert severity="error" role="alert">
          {t.firstAccess.unsupportedStep}
        </Alert>
        {backButton}
      </Stack>
    )
  }

  if (stage.kind === 'emailVerify') {
    const enviado = emailStart.isSuccess
    return (
      <Stack spacing={2}>
        <Typography variant="h2" component="h1">
          {t.firstAccess.emailVerifyTitle}
        </Typography>
        {!enviado && (
          <>
            <Typography variant="body2">{t.firstAccess.emailVerifyIntro}</Typography>
            {emailStart.isError && (
              <Alert severity="error" role="alert">
                {stepErrorMessage(emailStart.error, false)}
              </Alert>
            )}
            <Button
              variant="contained"
              disabled={emailStart.isPending}
              onClick={() => emailStart.mutate({ ticket: stage.ticket.ticket })}
            >
              {emailStart.isPending ? t.common.loading : t.firstAccess.emailSend}
            </Button>
          </>
        )}
        {enviado && (
          <Box component="form" onSubmit={submitEmailCode} noValidate>
            <Stack spacing={2}>
              <Alert severity="info">{t.firstAccess.emailSentInfo}</Alert>
              {emailConfirm.isError && (
                <Alert severity="error" role="alert">
                  {stepErrorMessage(emailConfirm.error, true, true)}
                </Alert>
              )}
              <TextField
                label={t.firstAccess.emailCodeLabel}
                autoComplete="one-time-code"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                slotProps={{ htmlInput: { maxLength: 9 } }}
                required
                fullWidth
                autoFocus
              />
              <Button
                type="submit"
                variant="contained"
                disabled={normalizeCode(code).length !== 8 || emailConfirm.isPending}
              >
                {emailConfirm.isPending ? t.common.loading : t.firstAccess.emailConfirmSubmit}
              </Button>
            </Stack>
          </Box>
        )}
        {backButton}
      </Stack>
    )
  }

  if (stage.kind === 'password') {
    return (
      <Box component="form" onSubmit={submitPassword} noValidate>
        <Stack spacing={2}>
          <Typography variant="h2" component="h1">
            {t.firstAccess.passwordTitle}
          </Typography>
          <Typography variant="body2">{t.firstAccess.passwordIntro}</Typography>
          {change.isError && (
            <Alert severity="error" role="alert">
              {stepErrorMessage(change.error, false)}
            </Alert>
          )}
          {mismatch && (
            <Alert severity="error" role="alert">
              {t.firstAccess.passwordMismatch}
            </Alert>
          )}
          <TextField
            label={t.firstAccess.newPasswordLabel}
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText={t.firstAccess.passwordRules}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label={t.firstAccess.confirmPasswordLabel}
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            fullWidth
          />
          <Typography variant="caption" color="text.secondary">
            {t.firstAccess.timeHint}
          </Typography>
          <Button
            type="submit"
            variant="contained"
            disabled={!newPassword || !confirmPassword || change.isPending}
          >
            {change.isPending ? t.common.loading : t.firstAccess.passwordSubmit}
          </Button>
          {backButton}
        </Stack>
      </Box>
    )
  }

  if (stage.kind === 'enroll') {
    const uri = enroll.data?.otpauthUri
    const secret = uri ? secretFromOtpauth(uri) : null
    return (
      <Stack spacing={2}>
        <Typography variant="h2" component="h1">
          {t.firstAccess.enrollTitle}
        </Typography>
        {!uri && (
          <>
            <Typography variant="body2">{t.firstAccess.enrollIntro}</Typography>
            {enroll.isError && (
              <Alert severity="error" role="alert">
                {stepErrorMessage(enroll.error, false)}
              </Alert>
            )}
            <Button
              variant="contained"
              disabled={enroll.isPending}
              onClick={() => enroll.mutate({ ticket: stage.ticket.ticket })}
            >
              {enroll.isPending ? t.common.loading : t.firstAccess.enrollStart}
            </Button>
          </>
        )}
        {uri && (
          <Box component="form" onSubmit={submitConfirm} noValidate>
            <Stack spacing={2} alignItems="stretch">
              <Box sx={{ alignSelf: 'center', p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <TotpQr uri={uri} label={t.firstAccess.qrLabel} />
              </Box>
              {secret && (
                <Typography variant="body2">
                  {t.firstAccess.manualKeyLabel}{' '}
                  <Box component="code" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {groupSecret(secret)}
                  </Box>
                </Typography>
              )}
              {confirm.isError && (
                <Alert severity="error" role="alert">
                  {stepErrorMessage(confirm.error, true)}
                </Alert>
              )}
              <TextField
                label={t.firstAccess.confirmCodeLabel}
                autoComplete="one-time-code"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                slotProps={{ htmlInput: { maxLength: 7 } }}
                required
                fullWidth
              />
              <Button
                type="submit"
                variant="contained"
                disabled={normalizeCode(code).length !== 6 || confirm.isPending}
              >
                {confirm.isPending ? t.common.loading : t.firstAccess.confirmSubmit}
              </Button>
            </Stack>
          </Box>
        )}
        {backButton}
      </Stack>
    )
  }

  if (stage.kind === 'codes') {
    return (
      <Stack spacing={2}>
        <Typography variant="h2" component="h1">
          {t.firstAccess.codesTitle}
        </Typography>
        <Alert severity="warning">
          {stage.user ? t.firstAccess.codesIntroEmail : t.firstAccess.codesIntro}
        </Alert>
        <Box
          component="ol"
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1,
            m: 0,
            pl: 3,
            fontFamily: 'monospace',
            fontSize: '1rem',
          }}
        >
          {stage.codes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => copyCodes(stage.codes)}>
            {copied ? t.firstAccess.copied : t.firstAccess.copy}
          </Button>
          <Button variant="outlined" onClick={() => downloadCodes(stage.codes)}>
            {t.firstAccess.download}
          </Button>
        </Stack>
        <FormControlLabel
          control={<Checkbox checked={saved} onChange={(e) => setSaved(e.target.checked)} />}
          label={t.firstAccess.savedCheck}
        />
        <Button variant="contained" disabled={!saved} onClick={leaveCodes}>
          {t.firstAccess.codesContinue}
        </Button>
      </Stack>
    )
  }

  return (
    <Box component="form" onSubmit={submitSignin} noValidate>
      <Stack spacing={2}>
        <Typography variant="h2" component="h1">
          {t.firstAccess.signinTitle}
        </Typography>
        <Typography variant="body2">
          {stage.email ? t.login.codeHintEmail : t.firstAccess.signinIntro}
        </Typography>
        {signin.isError && (
          <Alert severity="error" role="alert">
            {stepErrorMessage(signin.error, true)}
          </Alert>
        )}
        <TextField
          label={t.login.codeLabel}
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          slotProps={{ htmlInput: { maxLength: 16 } }}
          helperText={t.login.codeRecoveryHint}
          required
          fullWidth
          autoFocus
        />
        <Button
          type="submit"
          variant="contained"
          disabled={normalizeCode(code).length < 6 || signin.isPending}
        >
          {signin.isPending ? t.common.loading : t.firstAccess.signinSubmit}
        </Button>
        {backButton}
      </Stack>
    </Box>
  )
}
